import React, {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MacroParts } from '../components/MacroInput/types/MacroParts';

const SIDABAR_WIDTH = 280;

export interface IMacroOptionsContext {
  readonly segments: (string | MacroParts)[];
  readonly author?: string;
  readonly isOpen: boolean;
  readonly width: number;

  update: Dispatch<SetStateAction<Omit<IMacroOptionsContext, 'update'>>>;
}

const initialState = {
  segments: undefined,
  author: undefined,
  isOpen: false,
  width: SIDABAR_WIDTH,
};

export const MacroOptionsContext = createContext<IMacroOptionsContext>({
  ...initialState,
  update: () => undefined,
});

export const useMacroOptionsContext = () => useContext(MacroOptionsContext);

export const MacroOptionsContextWrap: FC<PropsWithChildren> = (props: PropsWithChildren<any>) => {
  const [state, setState] = useState<Omit<IMacroOptionsContext, 'update'>>(initialState);
  const isMounted = useRef(false);
  const contextValue = useMemo(
    () => ({
      ...state,
      update: (newState) => {
        // only update the state if mounted; otherwise, this warning appears:
        // "Can't perform a React state update on an unmounted component."
        if (isMounted.current) {
          setState(newState);
        }
      },
    }),
    [state],
  );

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <MacroOptionsContext.Provider value={contextValue}>
      {props.children}
    </MacroOptionsContext.Provider>
  );
};
